import React, { useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';

import { locationsIDs } from '@constants';
import { useContextReference } from '@hooks';
import { PageProvider, userSettingsState } from '@store';

import { AddLocationPanel } from './AddLocationPanel';
import { EditLocationPanel } from './EditLocationPanel';
import { LocationsListPanel } from './LocationsListPanel';

import { FOCUS_NOW } from '../../../helpers/Tools';
import { segmentEventTypes } from '../../segment/segmentEventTypes';
import { segmentTrackEvent } from '../../segment/SegmentTrackEvent';

const MAX_LOCATIONS = 5;

export const LocationManager = () => (
  <PageProvider
    ids={[locationsIDs.userLocationsList, locationsIDs.addNewLocationTextInput, locationsIDs.editLocationTextInput]}
  >
    <Locations />
  </PageProvider>
);

export const Locations = () => {
  const [userSettings, setUserSettings] = useRecoilState(userSettingsState);

  const [locationsData, setLocationsData] = useState(userSettings.locations);

  useEffect(() => {
    const newSettings = { ...userSettings };

    newSettings.locations = locationsData;
    setUserSettings(newSettings);
    //setPersistentStorage(`userSettings`, newSettings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationsData]);

  const listPanelFocusTarget = useContextReference(locationsIDs.userLocationsList);
  const addPanelFocusTarget = useContextReference(locationsIDs.addNewLocationTextInput);
  const editPanelFocusTarget = useContextReference(locationsIDs.editLocationTextInput);

  const [visiblePanel, setVisiblePanel] = useState({
    key: 'list',
    locationId: null,
  });

  const lastSetPanel = useRef('');

  useEffect(() => {
    setTimeout(() => {
      if (visiblePanel.key !== lastSetPanel.current) {
        lastSetPanel.current = visiblePanel.key;
        switch (visiblePanel.key) {
          case 'add': {
            FOCUS_NOW(addPanelFocusTarget);
            break;
          }
          case 'edit': {
            FOCUS_NOW(editPanelFocusTarget);
            break;
          }
          case 'list': {
            FOCUS_NOW(listPanelFocusTarget);
            break;
          }
        }
      }
    }, 10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiblePanel]);

  switch (visiblePanel.key) {
    case 'add': {
      return (
        <AddLocationPanel
          addNewLocation={newLocation => {
            const newData = [...locationsData];

            if (newData.length === MAX_LOCATIONS) {
              newData.splice(-1, 1);
            }
            newData.splice(1, 0, newLocation);
            if (newData.findIndex(location => location.isWatching) < 0) {
              newData[0].isWatching = true;
            }
            setLocationsData(newData);
          }}
          currentLocationNames={locationsData.map(location => location.name)}
          setVisiblePanel={setVisiblePanel}
          userSettings={userSettings}
        />
      );
    }
    case 'edit': {
      return (
        <EditLocationPanel
          data={locationsData}
          locationID={visiblePanel.locationId}
          setLocationName={(id, name) => {
            const newData = locationsData.map(location => (location.id === id ? { ...location, name } : location));

            setLocationsData(newData);
          }}
          setVisiblePanel={setVisiblePanel}
        />
      );
    }
    case 'list': {
      return (
        <LocationsListPanel
          data={locationsData}
          deleteLocation={index => {
            if (index >= 0 && index < locationsData.length) {
              const newData = [...locationsData];

              newData.splice(index, 1);
              if (newData.findIndex(location => location.isWatching) < 0) {
                newData[0] = {
                  ...newData[0],
                  isWatching: true,
                };
              }
              setLocationsData(newData);
            }
          }}
          setDefault={index => {
            if (index >= 0 && index < locationsData.length) {
              const locationAtIndex = {
                ...locationsData[index],
                isDefault: true,
              };
              const newData = [...locationsData].map(location => ({
                ...location,
                isDefault: false,
              }));

              newData.splice(index, 1);
              newData.unshift(locationAtIndex);
              newData[0].isDefault = true;
              setLocationsData(newData);

              const nextLocation = `${newData[0]?.state?.toLowerCase()}${newData[0]?.city?.replace(/\s/g, '')}`;

              segmentTrackEvent(segmentEventTypes.DEFAULT_LOCATION, userSettings, {
                manageLocations: {
                  nextLocation,
                },
              });
            }
          }}
          setVisiblePanel={setVisiblePanel}
          setWatching={index => {
            if (index >= 0 && index < locationsData.length) {
              const newData = [...locationsData].map(location => ({
                ...location,
                isWatching: false,
              }));

              newData[index].isWatching = true;

              setLocationsData(newData);

              const newUserSettings = { ...userSettings, locations: newData };

              segmentTrackEvent(segmentEventTypes.LOCATION_WATCHING, newUserSettings);
            }
          }}
          userSettings={userSettings}
        />
      );
    }
  }
};
